import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash, Sparkle, CheckCircle, Warning, XCircle, ArrowLeft } from "@phosphor-icons/react";

const TIMES = ["mañana", "mediodia", "tarde", "noche"];
const FREQS = ["diaria", "dias_alternos", "semanal"];

const STATUS_MAP = {
    safe: { cls: "bg-[var(--sage)] text-white border-[var(--sage)]", Icon: CheckCircle, label: "Seguro" },
    caution: { cls: "bg-[var(--amber)] text-white border-[var(--amber)]", Icon: Warning, label: "Precaución" },
    unsafe: { cls: "bg-[var(--terra-2)] text-white border-[var(--terra-2)]", Icon: XCircle, label: "Desaconsejado" },
};

export default function ScheduleBuilder() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [formulas, setFormulas] = useState([]);
    const [patients, setPatients] = useState([]);

    const [name, setName] = useState("Cronograma " + new Date().toLocaleDateString("es-ES"));
    const [patientId, setPatientId] = useState(searchParams.get("patient") || "");
    const [items, setItems] = useState([]);  // [{formula_id, time_of_day, frequency, start_date, end_date}]
    const [notes, setNotes] = useState("");
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/formulas").then((r) => setFormulas(r.data.filter((f) => f.feasibility !== "rejected")));
        api.get("/patients").then((r) => setPatients(r.data));
    }, []);

    const addItem = () => {
        if (!formulas.length) return;
        setItems((p) => [...p, { formula_id: formulas[0].id, time_of_day: "mañana", frequency: "diaria", start_date: "", end_date: "" }]);
        setAnalysis(null);
    };
    const updItem = (i, k, v) => {
        setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
        setAnalysis(null);
    };
    const rmItem = (i) => {
        setItems((p) => p.filter((_, idx) => idx !== i));
        setAnalysis(null);
    };

    const analyze = async () => {
        if (items.length < 2) { toast.error("Añade al menos 2 fórmulas"); return; }
        setAnalyzing(true);
        try {
            const { data } = await api.post("/schedules/analyze", {
                patient_id: patientId || null,
                items,
            });
            setAnalysis(data);
            const map = { safe: "success", caution: "warning", unsafe: "error" };
            toast[map[data.status] || "info"](`Análisis: ${data.summary || data.status}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error analizando");
        } finally { setAnalyzing(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.post("/schedules", {
                name,
                patient_id: patientId || null,
                items,
                notes: notes || null,
            });
            toast.success("Cronograma guardado");
            navigate(`/cronogramas/${data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando");
        } finally { setSaving(false); }
    };

    const statusBadge = analysis ? STATUS_MAP[analysis.status] || STATUS_MAP.caution : null;
    const SI = statusBadge?.Icon;

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow="Cronograma multi-fórmula"
                title="Combinar fórmulas y validar seguridad"
                lead="Añade varias fórmulas con su pauta horaria. Claude calculará dosis acumuladas, detectará interacciones y devolverá un veredicto de seguridad."
            >
                <div className="mt-4">
                    <Link to="/cronogramas" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]">
                        <ArrowLeft size={14}/> Volver
                    </Link>
                </div>
            </PageHeader>

            <div className="px-8 md:px-12 py-8 max-w-6xl space-y-6">
                <div className="border hairline bg-white p-6 grid lg:grid-cols-2 gap-4">
                    <Field label="Nombre del cronograma"><input className="i" value={name} onChange={(e)=>setName(e.target.value)} data-testid="schedule-name-input"/></Field>
                    <Field label="Paciente (opcional)">
                        <select className="i" value={patientId} onChange={(e)=>setPatientId(e.target.value)} data-testid="schedule-patient-select">
                            <option value="">— Sin paciente —</option>
                            {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </Field>
                </div>

                <div className="border hairline bg-white p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="label-eyebrow">Fórmulas del cronograma · {items.length}</div>
                        <button onClick={addItem} className="px-3 py-1.5 border hairline text-xs hover:bg-[var(--cream)] inline-flex items-center gap-1" data-testid="schedule-add-formula-button">
                            <Plus size={12}/> Añadir fórmula
                        </button>
                    </div>
                    {items.length === 0 && <div className="text-sm text-[var(--ink-3)] py-4">Empieza añadiendo fórmulas al cronograma.</div>}
                    <div className="space-y-3">
                        {items.map((it, i) => (
                            <div key={i} className="grid md:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-3 items-end p-3 bg-[var(--bone)] border hairline" data-testid={`schedule-item-${i}`}>
                                <Field label="Fórmula">
                                    <select className="i" value={it.formula_id} onChange={(e)=>updItem(i,"formula_id",e.target.value)} data-testid={`schedule-item-formula-${i}`}>
                                        {formulas.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                                    </select>
                                </Field>
                                <Field label="Horario">
                                    <select className="i" value={it.time_of_day} onChange={(e)=>updItem(i,"time_of_day",e.target.value)} data-testid={`schedule-item-time-${i}`}>
                                        {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </Field>
                                <Field label="Frecuencia">
                                    <select className="i" value={it.frequency} onChange={(e)=>updItem(i,"frequency",e.target.value)} data-testid={`schedule-item-freq-${i}`}>
                                        {FREQS.map((f) => <option key={f} value={f}>{f.replace("_"," ")}</option>)}
                                    </select>
                                </Field>
                                <Field label="Inicio"><input type="date" className="i" value={it.start_date} onChange={(e)=>updItem(i,"start_date",e.target.value)}/></Field>
                                <Field label="Fin"><input type="date" className="i" value={it.end_date} onChange={(e)=>updItem(i,"end_date",e.target.value)}/></Field>
                                <button onClick={()=>rmItem(i)} className="p-2 text-[var(--ink-3)] hover:text-[var(--terra-2)]" data-testid={`schedule-item-remove-${i}`}><Trash size={16}/></button>
                            </div>
                        ))}
                    </div>
                </div>

                <Field label="Notas"><textarea rows={2} className="i resize-none w-full" value={notes} onChange={(e)=>setNotes(e.target.value)} data-testid="schedule-notes-input"/></Field>

                <div className="flex gap-3 justify-between flex-wrap">
                    <button onClick={analyze} disabled={analyzing || items.length < 2} className="px-5 py-2.5 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2 disabled:opacity-50" data-testid="schedule-analyze-button">
                        <Sparkle size={14}/> {analyzing ? "Analizando…" : "Analizar seguridad"}
                    </button>
                    <button onClick={save} disabled={saving || items.length === 0} className="btn-primary disabled:opacity-50" data-testid="schedule-save-button">
                        {saving ? "Guardando…" : "Guardar cronograma"}
                    </button>
                </div>

                {analysis && (
                    <div className={`border hairline p-6 ${analysis.status === "unsafe" ? "bg-[var(--cream)]" : "bg-white"}`} data-testid="schedule-analysis-block">
                        <div className="flex items-center gap-3 mb-3">
                            <span className={`citation-badge ${statusBadge.cls}`}><SI size={12}/> {statusBadge.label}</span>
                            <span className="font-display text-lg">{analysis.summary}</span>
                        </div>
                        {analysis.cumulative_warnings?.length > 0 && (
                            <Block title="Dosis acumuladas críticas">
                                <ul className="text-sm space-y-1.5">
                                    {analysis.cumulative_warnings.map((w, i) => (
                                        <li key={i} className={w.exceeds ? "text-[var(--terra-2)]" : ""}>
                                            • <strong>{w.compound}</strong>: {w.total_mg} mg
                                            {w.ul_mg ? ` (UL: ${w.ul_mg} mg)` : ""} — {w.note}
                                        </li>
                                    ))}
                                </ul>
                            </Block>
                        )}
                        {analysis.interactions?.length > 0 && (
                            <Block title="Interacciones detectadas">
                                <ul className="text-sm space-y-1.5">{analysis.interactions.map((x, i) => <li key={i}>• {x}</li>)}</ul>
                            </Block>
                        )}
                        {analysis.timing_recommendations?.length > 0 && (
                            <Block title="Recomendaciones de timing">
                                <ul className="text-sm space-y-1.5">{analysis.timing_recommendations.map((x, i) => <li key={i}>• {x}</li>)}</ul>
                            </Block>
                        )}
                        {analysis.overall_recommendations?.length > 0 && (
                            <Block title="Recomendaciones generales">
                                <ul className="text-sm space-y-1.5">{analysis.overall_recommendations.map((x, i) => <li key={i}>• {x}</li>)}</ul>
                            </Block>
                        )}
                    </div>
                )}
            </div>
            <style>{`.i{width:100%;padding:8px 10px;border:1px solid var(--line);background:#fff;font-size:.85rem;outline:none}.i:focus{border-color:var(--sage)}`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (<div><label className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] block mb-1">{label}</label>{children}</div>);
}
function Block({ title, children }) {
    return (<div className="mt-4"><div className="label-eyebrow mb-2">{title}</div>{children}</div>);
}
